import { TextField, ToggleButton, ToggleButtonGroup } from '@ghs/components';
import { Box, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import SplDateRangePicker from './SplDateRangePicker';
import { FormErrors, SplFormByPieceNumber } from './types';
import LookupSearchFilters from './LookupSearchFilters.tsx';
import { useUnit } from 'effector-react';
import { $$theme } from '../../services/ThemeService/model.ts';

type LookupByPieceNumberProps = {
  formData: Partial<SplFormByPieceNumber>;
  errors: FormErrors;
  onChange: (value: Partial<SplFormByPieceNumber>) => void;
};

export default function LookupByPieceNumber(props: LookupByPieceNumberProps) {
  const { formData, errors, onChange } = props;
  const { palette } = useUnit($$theme.$palette);

  function handlePieceNumberType(_event: React.MouseEvent<HTMLElement>, pieceNumberType: SplFormByPieceNumber['pieceNumberType']): void {
    onChange({ pieceNumberType, imb: '', seqNum: '' });
  }

  function handleFormChange(event: React.ChangeEvent<HTMLInputElement>): void {
    onChange({ [event.target.name as keyof SplFormByPieceNumber]: event.target.value });
  }

  useEffect(() => {
    if (!formData.pieceNumberType) {
      onChange({ pieceNumberType: 'imb' });
    }
  }, [formData.pieceNumberType]);

  return (
    <Stack sx={{ gap: 2 }}>
      <ToggleButtonGroup color="primary" exclusive value={formData.pieceNumberType || 'imb'} onChange={handlePieceNumberType} sx={{ mb: 1, gap: 2 }}>
        <ToggleButton value="imb" sx={{ textTransform: 'none' }}>
          IMb
        </ToggleButton>
        <ToggleButton value="uniqueRecordId" sx={{ textTransform: 'none' }}>
          Unique Record ID
        </ToggleButton>
      </ToggleButtonGroup>
      <Box sx={{ display: 'flex', alignItems: 'center', pb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 0 }}>
          <span style={{ color: palette.error.main }}>*</span> Indicates a Required field
        </Typography>
      </Box>
      <Stack direction="row">
        {formData.pieceNumberType === 'imb' && (
          <TextField
            name="imb"
            label={
              <span>
                IMb (1st 20 digits){' '}
                <sup style={{ color: palette.error.main, fontSize: '1em' }} aria-label="This is a required field">
                  *
                </sup>
              </span>
            }
            value={formData.imb || ''}
            onChange={handleFormChange}
            error={Boolean(errors.imb)}
            helperText={errors.imb}
            inputProps={{ maxLength: 20, ['data-testid']: 'imb-input' }}
            sx={{ mr: 2, display: 'flex', flex: 1, maxWidth: 275 }}
          />
        )}
        {formData.pieceNumberType === 'uniqueRecordId' && (
          <TextField
            name="seqNum"
            label={
              <span>
                Unique Record ID{' '}
                <sup style={{ color: palette.error.main, fontSize: '1em' }} aria-label="This is a required field">
                  *
                </sup>
              </span>
            }
            value={formData.seqNum || ''}
            onChange={handleFormChange}
            error={Boolean(errors.seqNum)}
            helperText={errors.seqNum}
            inputProps={{ ['data-testid']: 'seqNum-input' }}
            sx={{ mr: 2, display: 'flex', flex: 1, maxWidth: 275 }}
          />
        )}
        <SplDateRangePicker formData={formData} errors={errors} />
      </Stack>
      <LookupSearchFilters formData={formData} errors={errors} onChange={onChange} />
    </Stack>
  );
}
